/**
 * Locale-aware numeric parsing, derived from `Intl.NumberFormat`.
 *
 * `Intl` formats numbers but cannot parse them, so the reverse direction is built by probing a
 * formatter for the glyphs it emits and undoing them. Pure and DOM-free.
 */

/** The locale-specific glyphs a formatted number can contain. */
export type NumberSymbols = {
  group: string
  decimal: string
  minusSign: string
  /** Currency symbol, percent sign and their spacing literals — removed before parsing. */
  strip: string[]
  /** Fixed text before the digits (`'$'` in en-CA), sign excluded. */
  prefix: string
  /** Fixed text after the digits (a no-break space then `'$'` in fr-CA). */
  suffix: string
}

/**
 * Every space a grouped number can carry, plus the one a keyboard produces. The grouping space is
 * locale-specific — `fr-CA` uses U+00A0 where a bare `fr` uses U+202F — but a person typing an
 * amount emits U+0020, so removing only the derived separator would reject the input we want.
 */
const SPACE_CHARS = [
  ' ', // U+0020 space — what a keyboard produces
  '\u00a0', // no-break space — groups fr-CA
  '\u202f', // narrow no-break space — groups fr
  '\u2009', // thin space
  '\u2007', // figure space
  '\t',
]

/** Keyboards emit U+002D; some locales format with U+2212. Both mean negative. */
const MINUS_CHARS = ['-', '\u2212']

/** Shape a partially-typed number must hold once reduced to plain ASCII. */
const PLAIN_NUMBER = /^-?\d*(\.\d*)?$/

const isDigit = (char: string) => char >= '0' && char <= '9'

const symbolsCache = new Map<string, NumberSymbols>()

/** The glyphs `locale` uses for the given format. Cached: formatters are expensive to build. */
export function deriveNumberSymbols(
  locale: string,
  options: Intl.NumberFormatOptions = {},
): NumberSymbols {
  const { style, currency, currencyDisplay } = options
  const cacheKey = `${locale}|${style ?? 'decimal'}|${currency ?? ''}|${currencyDisplay ?? ''}`
  const cached = symbolsCache.get(cacheKey)
  if (cached) return cached

  // Separators come off a plain decimal probe, not the caller's options: `maximumFractionDigits: 0`
  // would leave no `decimal` part to read, and forcing a minimum on can exceed their maximum.
  const separatorParts = new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: 1,
  }).formatToParts(-12345.6)

  const partValue = (type: Intl.NumberFormatPartTypes) =>
    separatorParts.find((part) => part.type === type)?.value

  // Currency and percent formats add a symbol and usually a spacing literal; both come back off.
  const strip =
    style === 'currency' || style === 'percent'
      ? new Intl.NumberFormat(locale, options)
          .formatToParts(0)
          .filter(
            (part) =>
              part.type === 'currency' || part.type === 'percentSign' || part.type === 'literal',
          )
          .map((part) => part.value)
      : []

  // Whatever sits outside the digits is fixed text the mask reproduces. The sign is left out: it
  // belongs with the number, and en-CA writes it ahead of the currency symbol.
  const affixParts = new Intl.NumberFormat(locale, options).formatToParts(0)

  const symbols: NumberSymbols = {
    group: partValue('group') ?? ',',
    decimal: partValue('decimal') ?? '.',
    minusSign: partValue('minusSign') ?? '-',
    strip,
    ...deriveAffixes(affixParts),
  }
  symbolsCache.set(cacheKey, symbols)
  return symbols
}

/** The fixed text around the digits of `parts`, sign excluded. */
function deriveAffixes(parts: Intl.NumberFormatPart[]): { prefix: string; suffix: string } {
  const firstDigit = parts.findIndex((part) => part.type === 'integer')
  const lastDigit = parts.reduce(
    (last, part, index) => (part.type === 'integer' || part.type === 'fraction' ? index : last),
    -1,
  )
  const joinRange = (from: number, to: number) =>
    parts
      .slice(from, to)
      .filter((part) => part.type !== 'minusSign')
      .map((part) => part.value)
      .join('')

  return {
    prefix: firstDigit === -1 ? '' : joinRange(0, firstDigit),
    suffix: lastDigit === -1 ? '' : joinRange(lastDigit + 1, parts.length),
  }
}

/** Denomination of a field's stored value, for fields that store scaled numbers. */
export type NumberScale = 'thousand' | 'million' | 'billion'

const SCALE_PROBES: Record<NumberScale, number> = {
  thousand: 1_000,
  million: 1_000_000,
  billion: 1_000_000_000,
}

/**
 * Symbols for a field whose stored value is denominated in `scale` — a targets field storing
 * millions, say. The affixes are probed off the locale's compact notation, so the abbreviation
 * lands where that locale writes it (`$1M` in en-CA, `1 M$` in fr-CA) while the value itself stays
 * unscaled: the field reads and writes `1` to mean one million.
 */
export function deriveScaledNumberSymbols(
  locale: string,
  options: Intl.NumberFormatOptions,
  scale: NumberScale,
): NumberSymbols {
  const { style, currency, currencyDisplay } = options
  const cacheKey = `${locale}|${style ?? 'decimal'}|${currency ?? ''}|${currencyDisplay ?? ''}|${scale}`
  const cached = symbolsCache.get(cacheKey)
  if (cached) return cached

  const base = deriveNumberSymbols(locale, options)
  const parts = getNumberFormatter(locale, {
    ...options,
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(SCALE_PROBES[scale])

  const affixes = deriveAffixes(parts)
  // en-CA writes its compact abbreviation flush against the digits (`$1M`); a breath of space reads
  // better on a value that means millions. Locales that already space it (fr-CA) are left alone.
  const suffix = /^\S/.test(affixes.suffix) ? `\u00a0${affixes.suffix}` : affixes.suffix

  const symbols: NumberSymbols = {
    ...base,
    ...affixes,
    suffix,
    // The abbreviation has to come off pasted text the way the currency symbol does.
    strip: [
      ...base.strip,
      ...parts.filter((part) => part.type === 'compact').map((part) => part.value),
    ],
  }
  symbolsCache.set(cacheKey, symbols)
  return symbols
}

const removeAll = (text: string, needle: string) =>
  needle === '' ? text : text.split(needle).join('')

/**
 * Reduce localised numeric text to plain ASCII (`-1234.56`) so a regex and `Number` can finish.
 *
 * The decimal separator is accepted as the locale's own, and as `.` wherever `.` is not that
 * locale's grouping separator — a French user reaching for the numpad dot is unambiguous under
 * `fr-CA`. Not symmetrical: `,` under `en-CA` would collide with grouping, where `1,500` has to
 * keep meaning 1500, so there it stays a separator and is dropped.
 */
export function toPlainNumber(text: string, symbols: NumberSymbols): string {
  // Order matters, and reduce keeps it: the mask's own glyphs, then every kind of space, then
  // whatever grouping separator is left (`,` in en-CA, `.` in de-DE).
  const stripped = [...symbols.strip, ...SPACE_CHARS, symbols.group].reduce(removeAll, text)

  const signed = [...MINUS_CHARS, symbols.minusSign].reduce(
    (out, minus) => (minus === '-' ? out : out.split(minus).join('-')),
    stripped,
  )

  return symbols.decimal === '.' ? signed : signed.split(symbols.decimal).join('.')
}

/**
 * The number `text` denotes, or `null` when it holds none — an empty field, a lone minus sign, or
 * anything unparseable. Never `NaN`, so callers have one "no value" to guard.
 */
export function parseLocaleNumber(text: string, symbols: NumberSymbols): number | null {
  const plain = toPlainNumber(text, symbols)
  if (plain === '' || !PLAIN_NUMBER.test(plain)) return null
  const value = Number(plain)
  return Number.isFinite(value) ? value : null
}

/**
 * Whether `text` is a number in progress, which is not the same as whether it parses: `'1,'` in
 * `fr-CA` parses to nothing yet is what someone typing `1,5` passes through, so rejecting it makes
 * the decimal unreachable. An empty string, a lone minus and a trailing separator are all valid.
 *
 * Grouping placement is not validated — `'1,,'` is accepted and reduces to 1 — since enforcing it
 * would reject the half-grouped text people paste.
 */
export function isValidPartialNumber(
  text: string,
  symbols: NumberSymbols,
  allowNegative: boolean,
  maxFractionDigits?: number,
): boolean {
  if (text === '') return true
  // Valid means nothing had to be thrown away: one grammar rather than two that can drift apart.
  return (
    sanitizePartialNumber(text, symbols, allowNegative, maxFractionDigits) ===
    toPlainNumber(text, symbols)
  )
}

/**
 * `text` reduced to the number it is trying to be: digits, at most one decimal separator, an
 * optional leading sign, and no more fraction digits than allowed.
 *
 * A masked field filters rather than refuses — reverting a controlled input after the event fights
 * the caret, while dropping the unusable character leaves it alone.
 *
 * Leading zeros are collapsed here rather than left for the mask: {@link maskNumber} groups through
 * `BigInt` and would drop them silently, leaving draft and rendered text disagreeing on how many
 * significant characters exist, which the caret arithmetic cannot survive.
 */
export function sanitizePartialNumber(
  text: string,
  symbols: NumberSymbols,
  allowNegative: boolean,
  maxFractionDigits?: number,
): string {
  const { out } = [...toPlainNumber(text, symbols)].reduce<{
    out: string
    hasSeparator: boolean
    fractionDigits: number
  }>(
    (acc, char) => {
      // A sign is only a sign in front.
      if (char === '-') {
        return allowNegative && acc.out === '' ? { ...acc, out: '-' } : acc
      }
      if (char === '.') {
        if (acc.hasSeparator || maxFractionDigits === 0) return acc
        return { ...acc, out: `${acc.out}.`, hasSeparator: true }
      }
      if (!isDigit(char)) return acc
      if (acc.hasSeparator) {
        if (maxFractionDigits !== undefined && acc.fractionDigits >= maxFractionDigits) return acc
        return { ...acc, out: acc.out + char, fractionDigits: acc.fractionDigits + 1 }
      }
      return { ...acc, out: acc.out + char }
    },
    { out: '', hasSeparator: false, fractionDigits: 0 },
  )
  return collapseLeadingZeros(out)
}

/**
 * `007` to `7`, `00` to `0`, `000.5` to `0.5` — a single zero survives on its own because it is a
 * number, not padding.
 */
function collapseLeadingZeros(plain: string): string {
  const sign = plain.startsWith('-') ? '-' : ''
  const body = sign === '' ? plain : plain.slice(1)
  const separator = body.indexOf('.')
  const integer = separator === -1 ? body : body.slice(0, separator)
  const rest = separator === -1 ? '' : body.slice(separator)

  if (integer.length < 2) return plain
  const trimmed = integer.replace(/^0+/, '')
  return `${sign}${trimmed === '' ? '0' : trimmed}${rest}`
}

/**
 * Grouped through `Intl` rather than every three characters, since grouping is not uniform across
 * locales. `BigInt` so a pasted run past `Number.MAX_SAFE_INTEGER` still groups correctly.
 */
function groupIntegerDigits(digits: string, locale: string, useGrouping: boolean): string {
  if (digits === '') return ''
  try {
    return getNumberFormatter(locale, {
      style: 'decimal',
      useGrouping,
      maximumFractionDigits: 0,
    }).format(BigInt(digits))
  } catch {
    return digits
  }
}

/**
 * Render plain digits as the locale would write them — the inverse of {@link toPlainNumber}.
 *
 * The fraction is reproduced as typed rather than run through a formatter, which would drop a
 * trailing separator the moment it was pressed and swallow the `0` in `1.0` on the way to `1.05`.
 */
export function maskNumber(
  plain: string,
  locale: string,
  symbols: NumberSymbols,
  useGrouping = true,
): string {
  if (plain === '' || plain === '-') return plain === '-' ? symbols.minusSign : ''

  const isNegative = plain.startsWith('-')
  const body = isNegative ? plain.slice(1) : plain
  const separator = body.indexOf('.')
  const integerDigits = separator === -1 ? body : body.slice(0, separator)
  const fractionDigits = separator === -1 ? '' : body.slice(separator + 1)

  return [
    isNegative ? symbols.minusSign : '',
    symbols.prefix,
    groupIntegerDigits(integerDigits, locale, useGrouping),
    separator === -1 ? '' : symbols.decimal,
    fractionDigits,
    symbols.suffix,
  ].join('')
}

/**
 * Whether `char` survives into the plain number — a digit, the decimal separator, or a sign.
 * Mirrors {@link toPlainNumber}, including that `.` is only a separator where it is not the
 * locale's grouping character.
 */
export function isSignificantChar(char: string, symbols: NumberSymbols): boolean {
  if (isDigit(char)) return true
  if (char === symbols.decimal) return true
  if (char === '.' && symbols.group !== '.') return true
  return char === '-' || char === symbols.minusSign || char === '\u2212'
}

const formatterCache = new Map<string, Intl.NumberFormat>()

/** A cached `Intl.NumberFormat` — the fields this backs render on every keystroke. */
export function getNumberFormatter(
  locale: string,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  const cacheKey = `${locale}|${JSON.stringify(options)}`
  let formatter = formatterCache.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    formatterCache.set(cacheKey, formatter)
  }
  return formatter
}
